/**
 * Commentary.
 *
 * Composed, not canned. Real ball-by-ball writing is three parts — what the
 * bowler sent down, what the batter did with it, and the outcome — which is why
 * professional commentary never repeats itself across an innings:
 *
 *   "Just short of a good length and outside the off stump, Langer watches the
 *    ball carefully and lets it go through to the keeper."
 *   "Length ball outside off, he had enough space to free his arms. Wallops it
 *    over extra cover."
 *
 * Picking whole sentences from a list of ninety gives you ninety lines and a
 * feeling of repetition by the third over. Composing 20 deliveries × 14 shots ×
 * 8 tails gives a couple of thousand per outcome, and every fragment bank
 * refuses to repeat its own last pick.
 */

export interface CommentaryEvent {
  delivery: {
    bowlerId: string; strikerId: string;
    wide: boolean; noBall: boolean;
    runsOffBat: number; batBoundary: "FOUR" | "SIX" | null;
    byes: number; legByes: number; runsRun: number;
    bonusZoneId?: string;
    wicket?: { kind: string; dismissedBatterId: string };
  };
  derived: { legal: boolean; totalRuns: number };
}

export interface Context {
  bowler: string;
  batter: string;
  dots?: number;
  need?: number | null;
  ballsLeft?: number | null;
  zoneLabel?: string;
  lang?: Lang;
  /**
   * Which ball this is, so the wording is stable.
   *
   * Without it the feed re-rolled every line on every render, and the scorer
   * gets a render per snapshot — so a delivery described as "driven through the
   * covers" became "carved over point" a second later, and the same ball read
   * differently on the scorer's phone and a watcher's. The text is decoration
   * over a fixed event and should not move once written. Pass the ledger
   * sequence; anything stable and unique per ball works.
   */
  seed?: number;
}

export type Lang = "en" | "hi";

/* ── fragments ──────────────────────────────────────────────────────────── */

const EN = {
  /** What the bowler sent down. */
  ball: [
    "Good length on off stump",
    "Full and straight",
    "Back of a length outside off",
    "Angling in at the pads",
    "Short and wide",
    "Fuller in length, on middle",
    "Just short of a good length",
    "Nipping away outside off",
    "Slower ball, into the pitch",
    "Yorker length, at the toes",
    "Floated up outside off",
    "Dug in short",
    "On a length, straightening",
    "Wide of off stump",
    "Full toss, waist high",
    "Tossed up on middle and leg",
    "Skidding on, flat",
    "Length ball drifting in",
    "Around off, holding its line",
    "Bouncer, climbing at the ribs",
    "Cutter, gripping off the surface",
    "Arm ball, hurrying through",
    "Wide of the crease, angling across",
    "Round the wicket, into the rough",
  ],
  /** What the batter did, by outcome. */
  dot: [
    "and {bowler} holds the length, {batter} has to respect it",
    "and it is stopped dead under the eyes",
    "and {batter} covers the line and lets the ball come",
    "and that one holds its line and beats everything",
    "and a leading edge, but it falls short of the fielder",
    "and {batter} taps it down and calls no run",
    "and {batter} defends it back down the pitch",
    "and it is pushed straight to cover",
    "and {batter} shoulders arms",
    "beating the outside edge — {bowler} likes that",
    "and it thuds into the pad, no run",
    "and {batter} is late on it, inside edge onto the thigh",
    "and it is dead-batted into the ground",
    "and {batter} plays and misses",
    "blocked with soft hands",
    "and the fielder at point cuts it off",
    "and {batter} lets it go through to the keeper",
    "and it is patted back to the bowler",
    "and there is nothing there at all",
    "and it is squeezed out to backward point",
    "and {batter} is squared up. Uncomfortable, that",
    "and the fielder at midwicket does the rest",
    "and it is watched carefully all the way through",
    "and {batter} rides the bounce well",
  ],
  one: [
    "and they scamper through for a single",
    "and it is dropped into the leg side, easy one",
    "and {batter} works it off the hip and they cross",
    "and a push to mid-on brings them one",
    "and it is worked away to square leg for one",
    "nudged into the leg side, they take a single",
    "and {batter} pushes to cover and calls for one",
    "dabbed down to third man, one run",
    "and it is tucked off the hip, easy single",
    "driven to mid-off, they scamper through",
    "and {batter} steers it to point for a single",
    "clipped away, one to deep midwicket",
    "punched off the back foot, and there is one in it",
    "and it is chipped over the infield for a single",
    "and they steal one to the man at short fine leg",
  ],
  two: [
    "and they come back hard for the second",
    "and it is placed into the gap, comfortably two",
    "and it is driven into the gap, back for two",
    "placed wide of the fielder, comfortable couple",
    "and they run hard, two to deep cover",
    "worked into the deep, two more",
    "and {batter} finds the gap at midwicket, two",
    "and the fielder cuts it off on the rope. They will take two",
    "and they turn hard and come back for the second",
  ],
  three: [
    "and the sweeper cuts it off, but three of them",
    "and they run hard and turn it into three",
    "and it splits the fielders, three taken",
    "driven hard into the deep, three",
    "and they turn for the third — good running",
    "and there is three in it. Excellent running, that",
  ],
  four: [
    "and {batter} punches it off the back foot, four all the way",
    "and that is dismissed off the pads to the fine leg fence",
    "and the field is beaten by sheer timing",
    "and {batter} steps out and drives it past mid-off",
    "and it is helped on its way behind square",
    "and {batter} times it beautifully through the covers",
    "and that is cut away square of the wicket",
    "clipped off the pads and it races away",
    "and {batter} leans into the drive, all along the ground",
    "pulled through midwicket, no chance for the fielder",
    "and it flies off the edge past slip",
    "driven straight back past the bowler",
    "and {batter} carves it over point",
    "flicked off the toes, beats deep square",
    "and it is glided down to third man for four",
    "and {batter} reverse sweeps, and that is brave and brilliant",
    "and it is lofted over the infield, beating the man in the deep",
    "and {batter} opens the face and runs it down past a diving fielder",
    "and given width, {batter} takes full advantage",
    "and that is threaded between two fielders. Placement, not power",
    "and it is driven on the up, and the timing is immaculate",
  ],
  six: [
    "and {batter} clears the ropes with room to spare",
    "and that is struck flat and it never comes down in the ground",
    "and it is slog-swept into the crowd",
    "and {batter} gets under it and it sails over long-on",
    "and {batter} clears the front leg and launches it over long-on",
    "and it is picked up off the toes and deposited over square leg",
    "slog-swept into the stands",
    "and {batter} goes down the ground — that is enormous",
    "and it disappears over deep midwicket",
    "and {bowler} drops it short and pays the full price",
    "and {batter} swings through the line, over long-off",
    "heaved over wide mid-on, and it keeps going",
    "and {batter} is down on one knee, and it sails over the ropes",
    "and that is muscled over cow corner. A huge blow",
    "and {batter} picked the length early and cleared it with ease",
    "and there is not a fielder within thirty yards of that",
    "and that has gone out of the ground entirely",
  ],
  /** Optional colour, appended sometimes. */
  /** After a boundary. These assume runs were scored. */
  tailBig: [
    "",
    " The crowd is up.",
    " {bowler} will not enjoy that.",
    " That was in the slot, and it has been punished.",
    " Beautifully played.",
    " {bowler} shakes his head and walks back.",
    " That is the shot of the over.",
    " Nothing the fielder could do.",
    " He middled that one.",
    " {batter} is seeing it like a football.",
    " That will hurt.",
    " Timing, not power.",
    " Straight out of the middle.",
  ],
  /** After an ordinary ball. Nothing here presumes anything was scored. */
  tailQuiet: [
    "",
    " Sensible cricket.",
    " Pressure building here.",
    " Good running between the wickets.",
    " {bowler} is asking questions.",
    " Watchful stuff from {batter}.",
    " The field is doing its job.",
    " Nothing given away there.",
  ],
  bowled: [
    "and {batter} is bowled, the stumps are rearranged",
    "and that is timber! {batter} is bowled, off stump flattened",
    "BOWLED HIM! {bowler} knocks the stumps back and {batter} has to go.",
    "Through the gate! {batter} is bowled, and that is a beauty from {bowler}.",
    "Timber! {batter} plays around it and the off stump is flattened.",
    "BOWLED! {batter} misses, {bowler} does not. The stumps are in a mess.",
    "Cleaned him up! {batter} had no answer to that one.",
    "Castled! Middle stump out of the ground, and {batter} is on his way.",
    "Yorker! Right at the base of off stump, and {batter} is bowled.",
    "BOWLED! {batter} never saw it. The bails are somewhere behind the keeper.",
  ],
  caught: [
    "and it is a simple catch in the end. {batter} goes",
    "and {batter} is caught, the fielder settles under it",
    "CAUGHT! {batter} picks out the fielder and it is all over.",
    "Skied it — and taken safely. {batter} has to walk.",
    "CAUGHT! Thick outside edge, and {batter} is held comfortably.",
    "Up goes the ball, and down it comes safely. {batter} departs.",
    "Caught! One shot too many from {batter} off {bowler}.",
    "Taken at long-on! {batter} did not get all of it.",
    "Straight down the fielder's throat. A soft way for {batter} to go.",
    "Superb catch in the deep, right on the rope, and {batter} is gone.",
    "Held at mid-off! Brilliant reflexes, and {batter} has to walk.",
  ],
  lbw: [
    "LBW! The finger goes up and {batter} is on his way.",
    "Trapped in front! {bowler} appeals long and loud, and {batter} has to go.",
    "LBW! {batter} struck on the pad, and that was hitting middle.",
    "Plumb! {batter} was late on it and paid for it.",
    "LBW! Nipped back in and hit {batter} on the knee roll. Dead in front.",
  ],
  runOut: [
    "RUN OUT! A terrible mix-up, and {batter} is well short of his ground.",
    "Direct hit! {batter} cannot get back in time.",
    "RUN OUT! Sharp work in the field ends it for {batter}.",
    "Chaos between the wickets, and {batter} is the one to go.",
    "RUN OUT! {batter} wanted a second that was never there.",
  ],
  stumped: [
    "STUMPED! {batter} is out of his crease and the keeper is lightning.",
    "Stumped! {batter} beaten in the flight, and the bails are off in a flash.",
  ],
  hitWicket: [
    "HIT WICKET! {batter} has trodden on his own stumps.",
    "Extraordinary — {batter} falls back onto the stumps.",
  ],
  wicket: ["WICKET! {batter} is gone.", "That is the breakthrough. {batter} departs."],
  wide: [
    "and that has been called wide, too far outside off",
    "and {bowler} sprays it down the leg side. Wide",
    "Wide. {bowler} strays down the leg side.",
    "That is called wide — too far outside off.",
    "Wide signalled, an extra to the batting side.",
    "Sprayed down the leg side, and the umpire's arms go out.",
    "Too far across, and the umpire has no choice.",
    "Down the leg side, and that is an easy call.",
  ],
  noBall: [
    "and {bowler} has overstepped. No ball",
    "and the umpire calls it. No ball, and a free hit to come",
    "No ball! {bowler} has overstepped.",
    "No ball called — and that means a free hit coming up.",
    "Over the line, and {bowler} has to bowl that again.",
  ],
  bye: ["Beats everyone including the keeper — byes taken.",
        "Through to the boundary for byes. The keeper will not enjoy that."],
  legBye: ["Off the pad, and they steal a leg bye.",
           "Worked off the hip, leg byes signalled."],
  bonus: [
    "{zone}! That is worth extra — {batter} knew exactly where to aim.",
    "Into {zone}, and the bonus runs are added.",
    "{batter} finds {zone}. Bonus on top of the runs.",
  ],
};

/* Hinglish — how commentary actually sounds on a subcontinental broadcast:
   English structure with Hindi/Urdu colour, which is what a Rawalpindi crowd
   would recognise rather than formal literary Hindi. */
const HI = {
  ball: [
    "Good length gend off stump par",
    "Full aur seedhi",
    "Back of a length, off ke bahar",
    "Pad par angle hoti hui",
    "Chhoti aur wide",
    "Fuller length, middle par",
    "Thodi si chhoti",
    "Off ke bahar nikalti hui",
    "Slower ball, pitch mein",
    "Yorker length, panjon par",
    "Hawa mein tossed up",
    "Short pitch, seene par",
    "Length gend, seedhi hoti hui",
    "Off stump se door",
    "Full toss, kamar tak",
    "Round the wicket, rough mein",
    "Arm ball, tezi se aati hui",
    "Cutter, pitch se grip karti hui",
  ],
  dot: [
    "aur {bowler} ne length par rakha, koi run nahi",
    "aur {batter} ne line cover karke chhoda",
    "aur gend ne line pakdi aur sabko beat kiya",
    "aur halka sa touch, lekin fielder tak nahi pahunchi",
    "aur {batter} ne solid defence khela",
    "aur seedha cover ke haath mein",
    "aur {batter} ne chhod diya",
    "aur bat ka bahri kinara chuk gaya — {bowler} khush",
    "aur pad par lagi, koi run nahi",
    "aur {batter} der se pahuncha, inside edge",
    "aur zameen par dead-bat kar diya",
    "aur {batter} play and miss",
    "aur point ne rok liya",
    "aur keeper tak chali gayi",
    "aur kuch bhi nahi mila is gend par",
    "aur {batter} bilkul comfortable nahi dikh raha",
  ],
  one: [
    "aur daud kar ek run le liya",
    "aur leg side mein dhakel kar ek run",
    "aur {batter} ne hip se ghumaya, ek run",
    "aur square leg ki taraf ek run",
    "leg side mein nudge, ek run mila",
    "aur {batter} ne cover ki taraf push karke ek liya",
    "third man ki taraf dab kar ek run",
    "aur hip se tuck karke aasan single",
    "aur infield ke upar se chip, ek run",
    "aur short fine leg ki taraf ek chura liya",
  ],
  two: [
    "aur doosre run ke liye tez daude",
    "aur gap mein rakha, aaram se do run",
    "aur gap mein drive, wapas aakar do run",
    "fielder se door rakha, aaram se do",
    "aur dono ne tez bhaag kar do run liye",
    "aur deep mein khela, do run",
  ],
  three: [
    "aur teen run daud kar le liye",
    "aur sweeper ne roka, lekin teen run ban gaye",
    "aur fielders ke beech se teen run",
    "deep mein drive, teen run",
  ],
  four: [
    "aur {batter} ne back foot punch mara, chauka",
    "aur pad se ghumaya, fine leg boundary paar",
    "aur timing itni acchi ki field bekaar ho gayi",
    "aur {batter} aage nikal kar mid-off ke paas se chauka",
    "aur {batter} ne covers ke beech se khubsurat timing ke saath chaar",
    "aur square cut, gend boundary ke paar",
    "pad se clip kiya aur gend daud gayi",
    "aur {batter} ne drive maara, zameen par hi rahi",
    "midwicket se pull, fielder ke paas koi mauka nahi",
    "aur edge se slip ke paas se nikal gayi",
    "aur seedha bowler ke paas se wapas",
    "aur {batter} ne point ke upar se carve kiya",
  ],
  six: [
    "aur {batter} ne aaram se ropes paar kar diya",
    "aur flat chhakka, maidan se bahar",
    "aur slog sweep, seedha crowd mein",
    "aur {batter} ne front leg hata kar long-on ke upar se uda diya",
    "aur panjon se utha kar square leg ke paar",
    "slog sweep, gend stands mein",
    "aur {batter} ne seedha ground ke bahar bhej diya — bahut lamba",
    "aur {bowler} ne chhoti daali aur poori keemat chukayi",
    "aur cow corner ke upar se. Zabardast shot",
    "aur gend ground ke bahar chali gayi",
  ],
  tail: ["", " Shaandaar.", " Crowd khada ho gaya.", " {bowler} ko pasand nahi aaya.",
         " Bahut badhiya running.", " Dabaav badh raha hai.", " Kya baat hai!"],
  tailBig: [
    "", " Shaandaar.", " Crowd khada ho gaya.", " {bowler} ko pasand nahi aaya.",
    " Kya baat hai!", " Perfect timing.", " Fielder kuch nahi kar sakta tha.",
    " Bilkul middle se.",
  ],
  tailQuiet: [
    "", " Samajhdari se khela.", " Dabaav badh raha hai.",
    " Bahut badhiya running.", " {bowler} sawal pooch raha hai.",
    " Kuch nahi diya is gend par.",
  ],
  bowled: [
    "aur {batter} bold ho gaya, stumps bikhar gaye",
    "aur off stump uda diya, {batter} bold",
    "BOLD! {bowler} ne stumps bikher diye, {batter} ko jaana padega.",
    "Gate ke beech se! {batter} bold, aur kya gend thi.",
    "Timber! {batter} ne galat line khela aur off stump gaya.",
    "Clean bold! {batter} ke paas koi jawab nahi tha.",
  ],
  caught: [
    "aur aasan catch. {batter} out",
    "aur fielder ne {batter} ka aaram se catch pakad liya",
    "CATCH! {batter} ne fielder ko dhoond liya, sab khatam.",
    "Hawa mein uchhaal diya — aur aasani se catch. {batter} ko jaana hoga.",
    "CATCH! Mota outside edge, aur {batter} ka catch pakad liya gaya.",
    "Long-on par catch! {batter} poori tarah connect nahi kar paya.",
  ],
  lbw: [
    "LBW! Ungli uthi aur {batter} chal diya.",
    "Front pe phansa! {bowler} ki lambi appeal, aur {batter} out.",
    "LBW! {batter} ke pad par lagi, middle stump ko ja rahi thi.",
  ],
  runOut: [
    "RUN OUT! Bura mix-up, aur {batter} crease se bahut door.",
    "Direct hit! {batter} wapas nahi pahunch paya.",
    "RUN OUT! Field mein tezi, aur {batter} ki paari khatam.",
  ],
  stumped: ["STUMP! {batter} crease se bahar tha aur keeper bijli ki tarah tez.",
            "Stump out! Flight mein chhala, bails gir gayin."],
  hitWicket: ["HIT WICKET! {batter} ne apne hi stumps par pair rakh diya."],
  wicket: ["WICKET! {batter} chala gaya.", "Breakthrough mil gaya. {batter} out."],
  wide: ["Wide. {bowler} leg side par bhatak gaya.",
         "Wide di gayi — off se bahut door.",
         "Wide signal, batting side ko ek extra."],
  noBall: ["No ball! {bowler} ne line paar kar di.",
           "No ball — aur ab free hit milega."],
  bye: ["Sabko chhakma de gayi, keeper bhi — byes."],
  legBye: ["Pad se lagi, leg bye le liya."],
  bonus: ["{zone}! Extra runs — {batter} ko pata tha kahan maarna hai.",
          "{zone} mein, aur bonus runs jud gaye."],
};

type Bank = typeof EN;
const BANKS: Record<Lang, Bank> = { en: EN, hi: HI as unknown as Bank };

/* ── the picker ─────────────────────────────────────────────────────────── */

const lastPick: Record<string, number> = {};

/**
 * FNV-1a. Small, fast, and good enough to scatter consecutive integers.
 *
 * The point is only that ball 41 and ball 42 land in unrelated places, so
 * consecutive deliveries do not march through the bank in order.
 */
function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function pick(
  lang: Lang, key: keyof Bank, vars: Record<string, string | number>, seed?: number,
): string {
  const bank = BANKS[lang][key] as string[] | undefined;
  if (!bank?.length) return "";

  // Seeded: the same ball reads the same way for as long as it exists, on
  // every device, through every re-render.
  if (seed !== undefined) {
    return fill(bank[hash(`${lang}:${String(key)}:${seed}`) % bank.length] ?? "", vars);
  }

  // Unseeded, for lines that are spoken once and never re-rendered.
  const memo = `${lang}:${String(key)}`;
  let i = Math.floor(Math.random() * bank.length);
  // Never the same fragment twice running from one bank. Four boundaries in an
  // over should read four different ways.
  if (bank.length > 1 && i === lastPick[memo]) i = (i + 1) % bank.length;
  lastPick[memo] = i;
  return fill(bank[i] ?? "", vars);
}

function fill(tpl: string, vars: Record<string, string | number>): string {
  return tpl.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? ""));
}

function tidy(s: string): string {
  return s.replace(/\s+/g, " ").replace(/\s+([,.!])/g, "$1").trim();
}

/* ── ball by ball ───────────────────────────────────────────────────────── */

export function describe(ev: CommentaryEvent, ctx: Context): string {
  const lang: Lang = ctx.lang ?? "en";
  const seed = ctx.seed;
  const d = ev.delivery;
  const vars = {
    bowler: ctx.bowler, batter: ctx.batter,
    runs: ev.derived.totalRuns, zone: ctx.zoneLabel ?? "the bonus zone",
  };

  // Wickets and extras are whole sentences: they are events, not shots.
  if (d.wicket) {
    const k = d.wicket.kind;
    const key: keyof Bank =
      k === "BOWLED" ? "bowled" : k === "CAUGHT" ? "caught" : k === "LBW" ? "lbw"
      : k === "RUN_OUT" ? "runOut" : k === "STUMPED" ? "stumped"
      : k === "HIT_WICKET" ? "hitWicket" : "wicket";
    return tidy(pick(lang, key, vars, seed));
  }
  if (d.bonusZoneId) return tidy(pick(lang, "bonus", vars, seed));
  if (d.wide) return tidy(pick(lang, "wide", vars, seed));
  if (d.noBall) return tidy(pick(lang, "noBall", vars, seed));
  if (d.byes) return tidy(pick(lang, "bye", vars, seed));
  if (d.legByes) return tidy(pick(lang, "legBye", vars, seed));

  // Everything else composes: delivery + shot + sometimes a tail.
  const runs = d.runsOffBat;
  const shotKey: keyof Bank =
    d.batBoundary === "SIX" ? "six" : d.batBoundary === "FOUR" ? "four"
    : runs === 3 ? "three" : runs === 2 ? "two" : runs === 1 ? "one" : "dot";

  const opener = pick(lang, "ball", vars, seed);
  const shot = pick(lang, shotKey, vars, seed);
  // Colour on the big moments, and occasionally elsewhere. Every ball carrying
  // a flourish reads like a machine.
  const big = shotKey === "six" || shotKey === "four";
  const flourish = seed === undefined
    ? Math.random() < 0.18
    : hash(`tail:${seed}`) % 100 < 18;
  const tail = big ? pick(lang, "tailBig", vars, seed)
             : flourish ? pick(lang, "tailQuiet", vars, seed) : "";

  const lead = shotKey === "six" ? "SIX! " : shotKey === "four" ? "FOUR! " : "";
  return tidy(`${lead}${opener}, ${shot}.${tail}`);
}

/* ── over and innings ───────────────────────────────────────────────────── */

export interface OverSummary {
  over: number; runs: number; wickets: number;
  score: number; wicketsDown: number;
  bowler: string; batting: string;
}

/** Read out at the end of every over — the thing a ground PA actually says. */
export function overCall(s: OverSummary, lang: Lang = "en"): string {
  const mood =
    s.runs === 0 ? (lang === "hi" ? "Maiden over!" : "A maiden over!")
    : s.runs <= 4 ? (lang === "hi" ? `Sirf ${s.runs} run is over mein.` : `Just ${s.runs} from the over.`)
    : s.runs >= 15 ? (lang === "hi" ? `${s.runs} run! Mehnga over.` : `${s.runs} from it — an expensive over.`)
    : (lang === "hi" ? `${s.runs} run is over se.` : `${s.runs} from the over.`);

  const wkt = s.wickets > 0
    ? (lang === "hi" ? ` Aur ${s.wickets} wicket bhi.` : ` And ${s.wickets} wicket${s.wickets === 1 ? "" : "s"} too.`)
    : "";

  const score = lang === "hi"
    ? `End of over ${s.over}. ${s.batting} ka score ${s.score} for ${s.wicketsDown}.`
    : `End of over ${s.over}. ${s.batting} are ${s.score} for ${s.wicketsDown}.`;

  return tidy(`${score} ${mood}${wkt}`);
}

/** Read out at the innings break, naming both sides and the target. */
export function targetCall(
  battedFirst: string, chasing: string, total: number, wickets: number,
  overs: string, target: number, lang: Lang = "en",
): string {
  if (lang === "hi") {
    return tidy(
      `${battedFirst} ne ${overs} over mein ${total} run banaye ${wickets} wicket par. ` +
      `${chasing} ko jeetne ke liye ${target} run chahiye.`,
    );
  }
  return tidy(
    `${battedFirst} finish on ${total} for ${wickets} from ${overs} overs. ` +
    `${chasing} need ${target} to win.`,
  );
}

/** Read out when the match ends. */
export function resultCall(summary: string, winner: string | null, lang: Lang = "en"): string {
  if (!winner) return lang === "hi" ? "Match tie ho gaya!" : "The match is tied!";
  return lang === "hi" ? `${winner} ${summary}!` : `${winner} ${summary}!`;
}

/**
 * The audio path, wired and waiting.
 *
 * Returns the clip names a spoken line would need. Nothing plays until files
 * exist under /sounds/say/<lang>/, at which point it works with no code change.
 */
export function speak(ev: CommentaryEvent, lang: Lang): string[] {
  const d = ev.delivery;
  const clips: string[] = [];
  if (d.wicket) clips.push("p-out");
  else if (d.batBoundary === "SIX") clips.push("p-six");
  else if (d.batBoundary === "FOUR") clips.push("p-four");
  else if (d.wide) clips.push("p-wide");
  else if (d.noBall) clips.push("p-noball");
  else if (ev.derived.totalRuns > 0) clips.push(`n-${ev.derived.totalRuns}`, "p-runs");
  else clips.push("p-dot");
  return clips.map((c) => `/sounds/say/${lang}/${c}.mp3`);
}

/** How many distinct lines each outcome can produce. Used by a test. */
export function variety(lang: Lang = "en"): Record<string, number> {
  const b = BANKS[lang];
  const balls = (b.ball as string[]).length;
  const tails = (b.tailBig as string[]).length + (b.tailQuiet as string[]).length;
  const out: Record<string, number> = {};
  for (const k of ["dot", "one", "two", "three", "four", "six"] as const) {
    out[k] = balls * (b[k] as string[]).length * tails;
  }
  for (const k of ["bowled", "caught", "lbw", "runOut", "wide", "noBall"] as const) {
    out[k] = (b[k] as string[]).length;
  }
  return out;
}
